<!-- eslint-disable max-len -->
<template>
  <div>
    <b-modal
      :id="`workorderAddModal${budgetYear.year}`"
      size="lg"
      @ok="saveWorkorder()"
      :ok-title='"Add"'
      :ok-disabled='!workorderAddModelValid'
    >
      <template v-slot:modal-title>New {{budgetYear.year}} Work Order</template>
      <b-overlay :show="formLoading">
        <VueFormGenerator
          :model="workorderAddModel"
          :schema="workorderAddSchema"
          :options="{
            validateAfterLoad: false,
            validateAfterChanged: true,
            validateAsync: true
          }"
          @validated="onWorkorderAddValidated"
          @model-updated='onWorkorderAddModelUpdated'
        />
        <div v-if='workorderAddModel && workorderAddModel.opportunityId !== null'>
          <b-card-body>
            <div class='details-label project-workorder-bills-items-details-label'>Services, Materials & Incentive Payments</div>
            <div v-if="workorderAddModel.workorderLineItems && workorderAddModel.workorderLineItems.length > 0">
              <div class='help-text'>
                <p>
                  <span>Select from planned items below, if desired.&nbsp;</span>
                  <span>Unit Cost and Total Cost per item will be calculated after the Work Order is created based on the Price Book set through the selected Opportunity.&nbsp;</span>
                  <span>You may add any unplanned items once this new Work Order has been saved.</span>
                </p>
                <p>
                  <span>Not seeing a scope-of-work item in this list?&nbsp;</span>
                  <span>Only work items that are available through the selected contract are displayed here.</span>
                </p>
              </div>
              <div v-if='totalAvailable &lt; totalEntered' class='project-workorder-lineitem-add-card-warning text-danger'>
                <span>Insufficient funds remain in the approved annual budget for this Work Order.&nbsp;</span>
                <span>Please ensure that the total cost does not exceed the remaining budget.</span>
              </div>
              <div class='project-workorder-lineitem-add-card-warning'>
                <b-row>
                  <b-col>
                    <span class='details-label project-workorder-bills-items-details-label'>Budgeted: </span>
                    <span class='project-workorder-bills-items-details-label'>
                      {{ totalBudgeted | toCurrency }}
                    </span>
                  </b-col>
                  <b-col>
                    <span class='details-label project-workorder-bills-items-details-label'>Obligated: </span>
                    <span class='project-workorder-bills-items-details-label'>
                      {{ totalObligated | toCurrency }}
                    </span>
                  </b-col>
                  <b-col>
                    <span class='details-label project-workorder-bills-items-details-label'>Available: </span>
                    <span v-bind:class="{'project-workorder-bills-items-details-label': true, 'text-danger': totalAvailable < 0}">
                      {{ totalAvailable | toCurrency }}
                    </span>
                  </b-col>
                  <b-col>
                    <span class='details-label project-workorder-bills-items-details-label'>Entered: </span>
                    <span class='project-workorder-bills-items-details-label'>
                      {{ totalEntered | toCurrency }}
                    </span>
                  </b-col>
                </b-row>
              </div>

              <b-table
                :fields="workorderAddModelServiceMaterialFields"
                :items="workorderAddModel.workorderLineItems"
                bordered
                hover
                responsive
                small
                striped
              >
                <template v-slot:cell(lineItemSelected)="row">
                  <b-form-checkbox
                    v-model="row.item.lineItemSelected"
                    @change='lineItemSelectedOnChange($event, row.item)'
                  >
                    {{row.item.lineItemProductName}}
                  </b-form-checkbox>
                </template>
                <template v-slot:cell(quantity)="row">
                  <b-form-input
                    v-if="workorderLineItemsSelected[`lineItemSelected_${budgetYear.year}_${row.item.id}`]"
                    v-model="row.item.quantity"
                    type='number'
                    class='form-control align-top align-right'
                    @input='lineItemQuantityOnInput($event, row.item)'
                    :state='row.item.quantity > 0'
                    :aria-describedby="`input-live-feedback_${budgetYear.year}_${row.item.id}`"
                  />
                  <b-form-input
                    v-else
                    v-model="row.item.quantity"
                    type='number'
                    class='form-control align-top align-right'
                    @input='lineItemQuantityOnInput($event, row.item)'
                    disabled
                  />
                  <b-form-invalid-feedback :id="`input-live-feedback_${budgetYear.year}_${row.item.id}`">Enter a quantity greater than 0</b-form-invalid-feedback>
                </template>
                <template v-slot:cell(lineItemOnOtherWorkorder)="row">
                  <b-form-checkbox v-model="row.item.lineItemOnOtherWorkorder" disabled/>
                </template>
              </b-table>
            </div>
            <div v-else>This Contract has no related Annual Budget Services and Materials!<br/><br/></div>
            <b-table
                :fields="workorderAddModelIncentivesFields"
                :items="workorderAddModel.incentivePayments"
                bordered
                hover
                responsive
                small
                striped
              >
                <template v-slot:cell(incentiveItemSelected)="row">
                  <b-form-checkbox
                    v-model="row.item.incentiveItemSelected"
                    @change='incentiveLineItemSelectedOnChange($event, row.item)'
                  >
                    {{row.item.incentiveTypeName}}
                  </b-form-checkbox>
                </template>
              </b-table>
          </b-card-body>
        </div>
        <template v-slot:modal-footer="{ ok, cancel }">
          <b-button size="sm" variant="danger" @click="cancel" class='btn-danger'>Cancel</b-button>
          <b-button size="sm" variant="success" @click="ok" class='btn-success'>Add</b-button>
        </template>
      </b-overlay>
    </b-modal>
  </div>
</template>
<script>
/* eslint-disable max-len, no-restricted-properties, padded-blocks, prefer-destructuring */
import { mapState } from 'vuex';
import FiscalYear from '@/mixins/fiscalYear';
import ProjectSchemaHelper from '@/mixins/projectSchemaHelper';

export default {
  name: 'ProjectWorkorderAdd',
  components: {},
  mixins: [
    FiscalYear,
    ProjectSchemaHelper,
  ],
  props: {
    budgetYear: {
      type: Object,
    },
    contactBriefs: {
      type: Array,
    },
    fundingSources: {
      type: Array,
    },
    opportunityBriefs: {
      type: Array,
    },
    organizationBriefs: {
      type: Array,
    },
    project: {
      type: Object,
    },
    projectBudget: {
      type: Object,
    },
  },
  computed: {
    ...mapState('Pricelist', {
      pricelistEntries: (e) => e.pricelistEntries,
    }),
    ...mapState('Project', {
      priceLists: (e) => e.priceLists,
      projectWorkorders: (e) => e.projectWorkorders,
      products: (e) => e.products,
      projectIncentivePayments: (e) => e.projectIncentivePayments,
    }),
    ...mapState('Workorder', {
      budgetModel: (e) => e.budgetModel,
      workorder: (e) => e.workorder,
    }),
    // totalEnteredFormatted() {
    //   return this.$options.filters.toCurrency(Number(this.totalEntered));
    // },
  },
  data() {
    return {
      formLoading: false,
      totalBudgeted: 0,
      totalObligated: 0,
      totalAvailable: 0,
      totalEntered: 0,
      workorderAddModel: null,
      workorderAddModelServiceMaterialFields: [
        {
          key: 'lineItemSelected',
          label: 'Cost Item',
          thClass: 'workorder-table-header',
        },
        {
          key: 'quantity',
          label: 'Quantity',
          thClass: 'workorder-table-header project-workorder-lineitems-quantity-header text-left',
          class: 'align-middle number-column',
        },
        {
          key: 'lineItemProductUnit',
          label: 'Units',
          formatter: (value) => {
            if (value) {
              return value;
            }
            return '';
          },
          thClass: 'workorder-table-header',
          class: 'align-middle',
        },
        {
          key: 'unitCost',
          label: 'Unit Cost',
          formatter: (value) => this.$options.filters.toCurrency(value),
          thClass: 'workorder-table-header',
          class: 'align-middle text-right currency-column',
        },
        {
          key: 'fundingSourceId',
          label: 'Funding Source',
          formatter: (fundingSourceId) => {
            let fundingSourceName = '';
            if (fundingSourceId) {
              const fundingSource = this.fundingSources.find((e) => e.recorD_ID === fundingSourceId);
              if (fundingSource) fundingSourceName = fundingSource.recorD_NAME;
            }
            return fundingSourceName;
          },
          thClass: 'workorder-table-header',
          class: 'align-middle',
        },
        {
          key: 'lineItemOnOtherWorkorder',
          label: 'On Other WO',
          thClass: 'workorder-table-header',
          class: 'align-middle text-center',
        },
      ],
      workorderAddModelIncentivesFields: [
        {
          key: 'incentiveItemSelected',
          label: 'Incentive Type',
          thClass: 'workorder-table-header',
        },
        {
          key: 'paymentAmount',
          label: 'Payment Amount',
          formatter: (value) => this.$options.filters.toCurrency(value),
          thClass: 'workorder-table-header',
          class: 'align-middle text-right',
        },
        {
          key: 'fundingSourceId',
          label: 'Funding Source',
          formatter: (fundingSourceId) => {
            let fundingSourceName = '';
            if (fundingSourceId) {
              const fundingSource = this.fundingSources.find((e) => e.recorD_ID === fundingSourceId);
              if (fundingSource) fundingSourceName = fundingSource.recorD_NAME;
            }
            return fundingSourceName;
          },
          thClass: 'workorder-table-header',
          class: 'align-middle',
        },
      ],
      workorderAddModelValid: false,
      workorderAddSchema: {},
      workorderLineItemsSelected: {},
      workorderIncentiveLineItemsSelected: {},
    };
  },
  mounted() {
    this.totalBudgeted = this.budgetModel.totalBudgeted;
    this.totalObligated = this.budgetModel.totalObligated;
    this.totalAvailable = (Math.round(this.totalBudgeted * 100) - Math.round(this.totalObligated * 100)) / Math.pow(10, 2);

    this.initWorkorderAddModel();
    this.workorderAddSchema = this.getProjectWorkorderAddSchema();

    this.$bvModal.show(`workorderAddModal${this.budgetYear.year}`);
  },
  methods: {
    initWorkorderAddModel() {
      const fiscalYear = this.budgetYear.year;
      const fiscalYearStart = new Date(`${fiscalYear - 1}`, 6, 1);
      const fiscalYearEnd = new Date(`${fiscalYear}`, 5, 30, 23, 59, 59, 999);

      const opportunityBriefs = this.opportunityBriefs.filter((e) => e.fiscalYear === fiscalYear);
      const projectBudgetYear = this.projectBudget.projectBudgetYears.find((e) => e.year === fiscalYear);

      let projectWorkorderLineItems = [];
      let budgetLineItems = [];

      if (this.projectWorkorders) {
        projectWorkorderLineItems = this.projectWorkorders.filter((e) => {
          const requestedDate = new Date(e.requestedDate);
          return (fiscalYearStart <= requestedDate) && (requestedDate < fiscalYearEnd);
        }).map((e) => e.workOrderLineItems).flat();
      }

      if (projectBudgetYear) {
        budgetLineItems = projectBudgetYear.projectBudgetLineItems.map((e) => ({ ...e }));
        budgetLineItems.forEach((e) => {
          e.lineItemSelected = false;
          this.$set(this.workorderLineItemsSelected, `lineItemSelected_${fiscalYear}_${e.id}`, false);
          e.lineItemProductName = null;
          e.lineItemProductUnit = null;
          if (e.product && e.product != null) {
            e.lineItemProductName = e.product.producT_NAME;
            const lineItemProductUnitCustomField = e.product.customfields.find((customfield) => customfield.fielD_NAME === 'Unit__c');
            if (lineItemProductUnitCustomField && lineItemProductUnitCustomField !== null) {
              e.lineItemProductUnit = lineItemProductUnitCustomField.fielD_VALUE;
            }
          }
          e.lineItemOnOtherWorkorder = false;
          const costItemLineItems = projectWorkorderLineItems.filter((projectWorkorderLineItem) => projectWorkorderLineItem.productId === e.costItemId);
          if (costItemLineItems.length > 0) {
            e.lineItemOnOtherWorkorder = true;
          }
        });
      }

      const nonCancelledCompletedWorkOrderIncentives = this.projectWorkorders
        .filter((wo) => wo.status !== 'Cancelled' && wo.status !== 'Completed').map(
          (e) => e.workOrderProjectIncentives,
        ).flat();

      const completedWorkOrderIncentives = this.projectWorkorders
        .filter((wo) => wo.status === 'Completed').map(
          (e) => e.workOrderProjectIncentives,
        ).flat();

      const billedCompletedWorkOrderIncentives = completedWorkOrderIncentives.filter((ip) => ip.paymentDate != null);
      const existingWorkOrderIncentives = [...new Set([...billedCompletedWorkOrderIncentives, ...nonCancelledCompletedWorkOrderIncentives])];

      const incentivePayments = this.projectIncentivePayments
        .filter((e) => this.getFiscalYear(e.dateDue) === fiscalYear)
        .filter((ip) => existingWorkOrderIncentives.filter((wopi) => wopi.projectIncentivePaymentId === ip.id).length === 0)
        .map((e) => {
          this.$set(this.workorderIncentiveLineItemsSelected, `incentiveSelected_${e.id}`, false);
          return {
            incentiveTypeName: e.incentiveType.name,
            paymentAmount: e.amountPaid,
            fundingSourceId: e.fundingSourceId,
            id: e.id,
            projectIncentivePaymentId: e.id,
            paymentDate: e.datePaid,
          };
        });

      this.workorderAddModel = {
        projectId: this.project.id,
        fiscalYear,
        fiscalYearStart,
        fiscalYearEnd,
        opportunityBriefs,
        budgetLineItems,
        opportunityId: null,
        workorderName: null,
        requestedDate: null,
        vendorTypes: ['Individual Contact', 'Organization'],
        vendorType: 'Individual Contact',
        vendorContact: null,
        vendorContactId: null,
        vendorContacts: this.contactBriefs,
        vendorOrganization: null,
        vendorOrganizationId: null,
        vendorOrganizations: this.organizationBriefs,
        contractorSpecialInstructions: null,
        workorderLineItems: [],
        incentivePayments,
      };
    },
    async lineItemQuantityOnInput(value, item) {
      /* eslint-disable-next-line no-param-reassign */
      item.quantity = Math.abs(Number(item.quantity));
      this.totalEntered = 0;

      const lineItemsSelected = this.workorderAddModel.workorderLineItems.filter((e) => e.lineItemSelected === true);

      if (lineItemsSelected.length > 0) {
        const totalsEntered = lineItemsSelected.map(
          (e) => Math.round(Number(e.quantity) * (Number(e.unitCost) * 100)),
        );
        const totalEntered = totalsEntered.reduce(
          (a, b) => a + b,
        );
        this.totalEntered = totalEntered;
      }

      this.totalEntered /= Math.pow(10, 2);

      this.validateWorkOrderAddFields([]);
    },
    async lineItemSelectedOnChange(value, item) {
      this.workorderLineItemsSelected[`lineItemSelected_${this.budgetYear.year}_${item.id}`] = value;
      this.totalEntered = 0;

      const incentivesSelected = this.workorderAddModel.incentivePayments.filter((e) => e.incentiveItemSelected === true);

      if (incentivesSelected.length > 0) {
        const totalsEntered = incentivesSelected.map(
          (e) => Math.round(Number(e.paymentAmount) * 100),
        );
        const totalEntered = totalsEntered.reduce(
          (a, b) => a + b,
        );
        this.totalEntered += totalEntered;
      }

      const lineItemsSelected = this.workorderAddModel.workorderLineItems.filter((e) => e.lineItemSelected === true && e.id !== item.id);

      if (lineItemsSelected.length > 0) {
        const totalsEntered = lineItemsSelected.map(
          (e) => Math.round(Number(e.quantity) * (Number(e.unitCost) * 100)),
        );
        const totalEntered = totalsEntered.reduce(
          (a, b) => a + b,
        );
        this.totalEntered = totalEntered;
      }

      if (value === true) {
        const totalEntered = Math.round(Number(item.quantity) * ((Number(item.unitCost) * 100)));
        this.totalEntered += totalEntered;
      }

      this.totalEntered /= Math.pow(10, 2);

      this.validateWorkOrderAddFields([]);
    },
    onWorkorderAddValidated(isValid, errors) {
      this.validateWorkOrderAddFields(errors);
    },
    async onWorkorderAddModelUpdated(fieldValue, fieldName) {
      const costItemIds = this.workorderAddModel.budgetLineItems.map((e) => e.costItemId);

      if (fieldName === 'opportunityId') {

        this.formLoading = true;
        const opportunity = this.workorderAddModel.opportunityBriefs.find((e) => e.id === fieldValue);
        await this.$store.dispatch('Pricelist/getPricelistEntries', opportunity.pricebookId);
        this.formLoading = false;

        const pricelistEntries = this.pricelistEntries.filter(
          (e) => costItemIds.includes(e.productId),
        );

        const productIds = pricelistEntries.map((e) => e.productId);
        const workorderLineItems = [...this.workorderAddModel.budgetLineItems.filter((e) => productIds.includes(e.costItemId))];
        workorderLineItems.forEach((e) => {
          if (e.product && e.product.producT_ID) {
            const pricelistEntry = pricelistEntries.find((p) => p.productId === e.product.producT_ID);
            if (pricelistEntry) {
              e.unitCost = pricelistEntry.price;
            }
          }
        });

        this.workorderAddModel.workorderLineItems = workorderLineItems;
      }

      if (fieldName === 'vendorType') {
        if (this.workorderAddModel.vendorType && this.workorderAddModel.vendorType === 'Individual Contact') this.workorderAddModel.vendorOrganization = null;
        if (this.workorderAddModel.vendorType && this.workorderAddModel.vendorType === 'Organization') this.workorderAddModel.vendorContact = null;
      }
    },
    saveWorkorder() {
      if (this.workorderAddModel.vendorContact) {
        this.workorderAddModel.vendorContactId = this.workorderAddModel.vendorContact.id;
      }
      if (this.workorderAddModel.vendorOrganization) {
        this.workorderAddModel.vendorOrganizationId = this.workorderAddModel.vendorOrganization.id;
      }

      const requestedDateYear = this.workorderAddModel.requestedDate.getFullYear();
      const requestedDateMonth = (this.workorderAddModel.requestedDate.getMonth() + 1);
      const requestedDateDay = this.workorderAddModel.requestedDate.getDate();

      const workorderInputModel = {
        projectId: this.workorderAddModel.projectId,
        status: this.workorderAddModel.status,
        opportunityId: this.workorderAddModel.opportunityId,
        workorderName: this.workorderAddModel.workorderName,
        requestedDateYear,
        requestedDateMonth,
        requestedDateDay,
        vendorContactId: this.workorderAddModel.vendorContactId,
        vendorOrganizationId: this.workorderAddModel.vendorOrganizationId,
        contractorSpecialInstructions: this.workorderAddModel.contractorSpecialInstructions,
        workOrderLineItems: this.workorderAddModel.workorderLineItems.filter(
          (e) => e.lineItemSelected === true,
        ).map((e) => {
          const workOrderLineItem = {
            costItemId: e.costItemId,
            fundingSourceId: Number(e.fundingSourceId),
            quantity: Number(e.quantity),
            unitCost: Number(e.unitCost),
            districtAndPartnerCost: (e.costShare),
          };
          return workOrderLineItem;
        }),
        workOrderProjectIncentivePayments: this.workorderAddModel.incentivePayments.filter(
          (e) => e.incentiveItemSelected === true,
        ).map((e) => {
          const incentive = {
            projectIncentivePaymentId: e.projectIncentivePaymentId,
            paymentDate: e.paymentDate,
            paymentAmount: e.paymentAmount,
          };
          return incentive;
        }),
      };
      this.$store.dispatch('Workorder/postWorkorder', workorderInputModel).then(() => {
        this.$router.push({ path: `/project/${this.project.id}/workorders/${this.workorder.id}`, params: { workOrderId: this.workorder.id } });
      });
    },
    validateWorkOrderAddFields(errors) {
      this.workorderAddModelValid = true;

      if (!this.workorderAddModel.workorderName || (this.workorderAddModel.workorderName === null) || (this.workorderAddModel.workorderName ?? '').length === 0) {
        console.warn('Invalid field: workorderName');
        this.workorderAddModelValid = false;
      }
      if (!this.workorderAddModel.requestedDate || (this.workorderAddModel.requestedDate === null)) {
        console.warn('Invalid field: requestedDate');
        this.workorderAddModelValid = false;
      }
      if (!this.workorderAddModel.opportunityId || this.workorderAddModel.opportunityId === null) {
        console.warn('Invalid field: opportunityId');
        this.workorderAddModelValid = false;
      }
      if (this.workorderAddModel.vendorType === 'Individual Contact') {
        if (!this.workorderAddModel.vendorContact || this.workorderAddModel.vendorContact === null) {
          console.warn('Invalid field: vendorContact');
          this.workorderAddModelValid = false;
        }
      } else if (!this.workorderAddModel.vendorOrganization || this.workorderAddModel.vendorOrganization === null) {
        console.warn('Invalid field: vendorOrganization');
        this.workorderAddModelValid = false;
      }
      if (!this.workorderAddModel.opportunityId || this.workorderAddModel.opportunityId === null) {
        console.warn('Invalid field: opportunityId');
        this.workorderAddModelValid = false;
      }
      if (this.workorderAddModel.workorderLineItems && this.workorderAddModel.workorderLineItems != null) {
        if (this.workorderAddModel.workorderLineItems.filter((e) => e.lineItemSelected && e.quantity <= 0).length > 0) {
          console.warn('Invalid field: quantity');
          this.workorderAddModelValid = false;
        }
      }
      if (this.totalAvailable < this.totalEntered) {
        console.warn('totalAvailable < totalEntered');
        this.workorderAddModelValid = false;
      }
      if (errors && errors.length > 0) {
        console.warn('errors: ', errors);
        this.workorderAddModelValid = false;
      }
    },
  },
};
</script>
